/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import { Timestamp } from 'firebase/firestore';

import Part, { partSchema } from './part.js';

export const fluidicLayerSchema = partSchema.shape({
  material: Yup.string().required('Material is required'),
  stepType: Yup.string(),
});

/**
 * @typedef {Yup.InferType<typeof fluidicLayerSchema>} FluidicLayerSchema
 * @typedef {BaseDocData & FluidicLayerSchema & { createdAt: Timestamp; updatedAt: Timestamp; }} FluidicLayerDocData
*/

/**
 * Class representing a fluidic layer.
 * @class
 * @extends Part
 */
export default class FluidicLayer extends Part {
  /** @type {FluidicLayerDocData['material']} */ material;
  /** @type {FluidicLayerDocData['stepType']} */ stepType;

  /**
   * Create a fluidic layer.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  constructor({
    material,
    stepType,
    ...props
  }) {
    const validatedData = fluidicLayerSchema.validateSync({
      material,
      stepType,
      ...props,
    });

    super(validatedData);

    this.material = validatedData.material;
    this.stepType = validatedData.stepType;
  }

  /**
   * Converts the {@link FluidicLayer} instance to Firestore document data.
   * @returns {Omit<FluidicLayerDocData, 'id'>} {@link FluidicLayerDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const baseDoc = super.toFirestore({ serverTimestamp });
    return {
      ...baseDoc,
      metadata: {
        ...baseDoc.metadata,
        material: this.material,
        stepType: this.stepType,
      },
    };
  }

  /**
   * Creates a {@link FluidicLayer} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns {FluidicLayer} A {@link FluidicLayer} instance.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  static fromFirestore(docData) {
    const {
      material,
      stepType,
      linkedParts,
      components,
      ...restMetadata
    } = docData.metadata || {};
    return new FluidicLayer({
      ...docData,
      material,
      stepType,
      linkedParts,
      components,
      metadata: restMetadata || {},
    });
  }
}
